/**
 * useFlyToCart — React hook for fly-to-cart animation.
 *
 * Wraps the FlyToCart vanilla module for use in React components.
 * Use this when your product widget is built with React.
 *
 * @param {React.RefObject} cartIconRef - Ref to the cart icon element
 * @returns {{ triggerFly: Function, isAnimating: boolean }}
 *
 * @example
 * const cartIconRef = useRef(null);
 * const { triggerFly, isAnimating } = useFlyToCart(cartIconRef);
 *
 * const handleAddToCart = (product) => {
 *   triggerFly(cardRef.current, product, () => {
 *     addToCartAPI(product);
 *   });
 * };
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import { FlyToCart } from './FlyToCart.js';

export function useFlyToCart(cartIconRef) {
  const [isAnimating, setIsAnimating] = useState(false);
  const flyToCartRef = useRef(null);

  useEffect(() => {
    flyToCartRef.current = new FlyToCart({
      getCartTarget: () => cartIconRef?.current ?? null,
      onCartBump: () => {},
      onRipple: () => {},
    });

    return () => {
      if (flyToCartRef.current) {
        flyToCartRef.current.destroy();
        flyToCartRef.current = null;
      }
    };
  }, [cartIconRef]);

  const triggerFly = useCallback((cardElement, product, onComplete) => {
    const instance = flyToCartRef.current;
    if (!instance) {
      if (onComplete) onComplete();
      return;
    }

    const prevAnimating = instance.isAnimating;
    if (prevAnimating) return;

    const checkAnimating = () => {
      setIsAnimating(instance.isAnimating);
    };

    const wrappedComplete = () => {
      checkAnimating();
      if (onComplete) onComplete();
    };

    setIsAnimating(true);
    instance.triggerFly(cardElement, product, wrappedComplete);
  }, []);

  return { triggerFly, isAnimating };
}
